import { useState } from 'react'
import { motion } from 'framer-motion'
import {
    LayoutDashboard, FileCheck, CheckCircle, FileText, BookOpen,
    MessageSquare, LogOut, ChevronLeft, ChevronRight, Sparkles,
    BarChart2, Megaphone, Trophy, CreditCard, HelpCircle, History, Brain
} from 'lucide-react'

import { useNavigate } from 'react-router-dom'

interface StudentSidebarProps {
    activeTab: string
    setActiveTab: (tab: string) => void
}

// Navigation structure grouped by category
const navGroups = [
    {
        label: 'Main',
        items: [
            { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
            { id: 'daily-plan', label: 'Daily Plan', icon: FileCheck },
            { id: 'discipline', label: 'Discipline', icon: CheckCircle },
        ]
    },
    {
        label: 'Learning',
        items: [
            { id: 'tests', label: 'Tests', icon: FileText },
            { id: 'test-history', label: 'Test History', icon: History },
            { id: 'study-material', label: 'Study Material', icon: BookOpen },
            { id: 'question-bank', label: 'Question Bank', icon: Brain },
            { id: 'performance', label: 'Performance', icon: BarChart2 },
        ]
    },
    {
        label: 'Community',
        items: [
            { id: 'chat', label: 'Chat with Mentor', icon: MessageSquare },
            { id: 'announcements', label: 'Announcements', icon: Megaphone },
            { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
        ]
    },
    {
        label: 'Account',
        items: [
            { id: 'payments', label: 'Payments', icon: CreditCard },
            { id: 'support', label: 'Support', icon: HelpCircle },
        ]
    },
]

import { useAuth } from '@/context/auth-context'

export function StudentSidebar({ activeTab, setActiveTab }: StudentSidebarProps) {
    const navigate = useNavigate()
    const { user, logout } = useAuth()
    const [isCollapsed, setIsCollapsed] = useState(false)

    // Helper to get initials
    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2)
    }

    return (
        <aside className={`hidden lg:flex flex-col shrink-0 h-full transition-all duration-300 ${isCollapsed ? 'w-[72px]' : 'w-[260px]'}`}>
            <div className="h-full flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 relative rounded-xl overflow-hidden shadow-sm">
                {/* Logo/Brand Header */}
                <div className="h-16 flex items-center gap-3 px-4 border-b border-slate-100 dark:border-slate-800">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-blue to-accent-gold flex items-center justify-center shrink-0">
                        <Sparkles className="h-5 w-5 text-white" />
                    </div>
                    {!isCollapsed && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="overflow-hidden"
                        >
                            <h1 className="font-display font-bold text-theme-primary text-base leading-tight">Future</h1>
                            <p className="text-[11px] text-theme-secondary -mt-0.5">Founder Academy</p>
                        </motion.div>
                    )}
                </div>

                {/* Collapse Toggle Button */}
                <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="absolute top-[22px] -right-3 w-6 h-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-all z-20"
                >
                    {isCollapsed ? (
                        <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
                    ) : (
                        <ChevronLeft className="h-3.5 w-3.5 text-slate-500" />
                    )}
                </button>

                {/* Navigation */}
                <nav className="flex-1 py-4 px-3 overflow-y-auto custom-scrollbar">
                    {navGroups.map((group, groupIdx) => (
                        <div key={group.label} className={groupIdx > 0 ? 'mt-5' : ''}>
                            {!isCollapsed && (
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 mb-2">
                                    {group.label}
                                </p>
                            )}
                            <div className="space-y-1">
                                {group.items.map((item) => {
                                    const Icon = item.icon
                                    const isActive = activeTab === item.id

                                    return (
                                        <button
                                            key={item.id}
                                            onClick={() => setActiveTab(item.id)}
                                            className={`
                                                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                                                ${isCollapsed ? 'justify-center' : ''}
                                                ${isActive
                                                    ? 'bg-primary-blue text-white shadow-md shadow-primary-blue/20'
                                                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                                                }
                                            `}
                                        >
                                            <Icon className={`h-5 w-5 shrink-0 ${isActive ? '' : 'opacity-70'}`} />
                                            {!isCollapsed && (
                                                <span className="truncate">{item.label}</span>
                                            )}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                </nav>

                {/* User Profile & Logout */}
                <div className="p-3 border-t border-slate-100 dark:border-slate-800">
                    {/* Profile Quick View */}
                    <button
                        onClick={() => setActiveTab('profile')}
                        className={`
                            w-full flex items-center gap-3 p-2.5 rounded-lg mb-2 transition-all
                            ${isCollapsed ? 'justify-center' : ''}
                            ${activeTab === 'profile'
                                ? 'bg-slate-100 dark:bg-slate-800'
                                : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                            }
                        `}
                    >
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center text-white font-semibold text-sm shrink-0">
                            {user ? getInitials(user.name) : 'ST'}
                        </div>
                        {!isCollapsed && (
                            <div className="text-left overflow-hidden">
                                <p className="text-sm font-medium text-theme-primary truncate">{user?.name || 'Student'}</p>
                                <p className="text-[11px] text-theme-secondary truncate">{user?.email || 'student@example.com'}</p>
                            </div>
                        )}
                    </button>

                    {/* Logout */}
                    <button
                        onClick={() => {
                            logout()
                            navigate('/')
                        }}
                        className={`
                            w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium 
                            text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all
                            ${isCollapsed ? 'justify-center' : ''}
                        `}
                    >
                        <LogOut className="h-4.5 w-4.5" />
                        {!isCollapsed && <span>Logout</span>}
                    </button>
                </div>
            </div>
        </aside>
    )
}
